/**
 * Helper: Cloudinary URL
 * ------------------------------------------------------------------------------
 * - Reusable class for generating a Cloudinary URL from specific inputs.
 *
 * @namespace CloudinaryURL
 */
import { zipObject } from 'lodash-es'
import {
  isTransformCode,
  getTransformByName,
  getTransformByCode,
} from './cloudinary-transform-map'
import { setByIndex, removeByIndex, safeMatch } from './utils'

/**
* Set step value based on a transform name. By default, this sets the value of all steps in all
* pipelines, but can be restricted to a particular pipeline by passing a pipelineIndex. If the transform does not exist,
* it is added at the end of the last pipeline.
* @param {Array} transformations - transformations array to be modified
* @param {String} name - named transform to modify
* @param {Any} value - transform value to set on matching steps
* @param {Number} pipelineIndex - restrict setting to a particular pipeline
* @returns {Array}
*/
function setStepValueByTransformName(transformations, name, value, pipelineIndex) {
  const matches = getTransformIndexesByName(transformations, name, pipelineIndex)
  if (!matches.length) {
    const lastIndex = transformations.length - 1
    const newItem = { ...getTransformByName(name), transformValue: value }
    const oldItems = transformations[lastIndex]
    const newPipeline = [...oldItems, newItem]
    return setByIndex(transformations, lastIndex, newPipeline)
  }
  return setStepValueByIndexes(transformations, matches.map(indexes =>
    ({ indexes, value })))
}

/**
 * Find pipelines that match a given tranform name, then remove any matching
 * ones
* @param {Array} transformations - transformations array to be modified
* @param {String} name - named transform to remove containing pipeline
* @param {Number} pipelineIndex - restrict removal to a particular pipeline
* @returns {Array}
*/
function removePipelineByTransformName(transformations, name, pipelineIndex) {
  const matches = getTransformIndexesByName(transformations, name, pipelineIndex)
  if (!matches.length) {
    return transformations
  }
  // reverse our matches so that the higher indexes are removed first, removing
  // lower indexes first would change dependent indexes
  const removePipelines = matches.reverse().reduce((pipelineIndexesToRemove, match) => {
    return pipelineIndexesToRemove.includes(match[0]) ?
      pipelineIndexesToRemove :
      [...pipelineIndexesToRemove, match[0]]
  }, [])
  return removePipelines.reduce((newTransformations, index) => {
    return removeByIndex(newTransformations, index)
  }, transformations)
}

/**
* Get the value of a transform step by name. By default, this gets the last matching value across
* pipelines, but can be restricted to a particular pipeline by passing a pipelineIndex
* @param {Array} transformations - transformations array to be searched
* @param {String} name - named transform to search for
* @param {Number} pipelineIndex - restrict search to a particular pipeline
* @returns {String}
*/
function getStepValueByTransformName(transformations, name, pipelineIndex) {
  if (pipelineIndex) {
    const match = transformations[pipelineIndex].find(step => {
      return step.name === name
    })
    return match || null

  }
  return transformations.reduce((prevVal, pipeline, index) => {
    const match = pipeline.find(step => {
      return step.name === name
    })
    return match || prevVal
  }, null)
}

/**
* Set values in transformation steps by 2D indexes
* @param {Array} transformations - transformations array to be modified
* @param {Array} indexedValues - indexes and values in the form [{value: $value,
* indexes: [$pipelineIndex, $stepIndex]}]
* @returns {Array}
*/
function setStepValueByIndexes(transformations, indexedValues) {
  return indexedValues.reduce((newTransformations, { value, indexes }) => {
    const transformationsToModify = newTransformations[indexes[0]] || []
    const stepToModify = transformationsToModify[indexes[1]]
    if (!transformationsToModify.length || !stepToModify) {
      return newTransformations
    }

    const newStep = {
      ...stepToModify,
      transformValue: value,
    }
    const newPipeline = setByIndex(transformationsToModify, indexes[1], newStep)

    return setByIndex(newTransformations, indexes[0], newPipeline)
  }, transformations)
}

/**
* Get 2D indexes where transformation name matches given
* @param {Array} transformations - transformations array to be searched
* @param {String} name - named transform to search for
* @param {Number} pipelineIndex - restrict search to a particular pipeline
* @returns {Array}
*/
function getTransformIndexesByName(transformations, name, pipelineIndex) {
  const getMatches = (pipeline, index) => pipeline.reduce((stepMatches, step, stepIndex) => {
    return step.name === name ? [...stepMatches, [index, stepIndex]] :
      stepMatches
  }, [])

  if (pipelineIndex) {
    const pi = pipelineIndex === -1 ? transformations.length - 1 :
      pipelineIndex
    return getMatches(transformations[pi], pi)
  }

  return transformations.reduce((matches, pipeline, pi) => {
    const newMatches = getMatches(pipeline, pi)
    return newMatches.length ? [...matches, ...newMatches] : matches
  }, [])
}

/**
* Get cloudinary transforms from a transformation string extracted from a URL
* @param {String} str - string to be parsed into transforms
* @returns {Array}
*/
function getCloudinaryTransforms(str) {
  const transforms = str.split(',')
  const tr = transforms.reduce((acc,item) => {
    const [transformCode, transformValue] = item.split('_')
    if (isTransformCode(transformCode)) {
      const name = getTransformByCode(transformCode).name
      return [...acc, { name, transformCode, transformValue }]
    }
    return acc
  }, [])

  return tr
}

// All cloudinary URL props, in order, with:
// name: string, human-readable name, used to find/reference transforms
// required: boolean, if property is required or not
// multiple: boolean, if property has multiple values or not
// extract: function, function to extract match from string
// matchValue: function, function to stringify value to compare to string

const deliveryTypeRegex = /(upload|private|authenticated|list|fetch|facebook|twitter|twitter_name|gravatar|youtube|hulu|vimeo|animoto|worldstarhiphop|dailymotion|multi|text|url2png|asset)/

function transformationPipelineToString(transform) {
  return transform.map(string => `${string.transformCode}_${string.transformValue}`).join(',')
}
const sequencedProperties = [
  { 'name': 'proto', required: false },
  { 'name': '_blank', required: false, extract: (match) => !match.length },
  { 'name': 'baseUrl', required: true, extract: safeMatch(/([a-z.-])+(\.[a-z]{1,3})+$/) },
  { 'name': 'cloudName', required: false, extract: safeMatch(/^(?!image|video|raw)[^.]+/) },
  { 'name': 'assetType', required: false, extract: safeMatch(/(image|video|raw)/) },
  { 'name': 'deliveryType', required: false, extract: safeMatch(deliveryTypeRegex) },
  { 'name': 'transformations', multiple: true, extract: getCloudinaryTransforms, matchValue: transformationPipelineToString },
  { 'name': 'version', extract: safeMatch(/v[0-9]+/) },
  { 'name': 'publicId', required: true, multiple:true, extract: safeMatch(/^[^.]+/) },
  { 'name': 'extension', required: false, extract: (str) => safeMatch(/\.([a-z0-9]+)$/)(str).substring(1) },
]

/**
 * Recursive function that take a split URL and array of properties, then loops through the properties and deconstructs
 * the URL piece by piece.
 * @param {Array} splitUrl - URL to destructure, split into an array on the "/" character
 * @param {Array} properties - Properties to destructure from the URL
 * @param {Object} res - Result object after each step of the destructure
 * @returns {Object}
 */
const destructure = (splitUrl, properties, res = {}) => {
  // We're done once all properties are processed
  if (!properties.length) return res

  const currentProperty = properties[0]

  // If the URL ends before all required properties are parsed, throw an error
  if (!splitUrl.length) {
    if (currentProperty.required) {
      throw new Error(`Property ${currentProperty.name} required in URL but not found`)
    }
    return destructure(splitUrl, properties.slice(1), res)
  }

  const [currentString, restOfUrl] = [splitUrl[0], splitUrl.slice(1)]
  const value = currentProperty.extract
    ? currentProperty.extract(currentString)
    : currentString

  if (currentProperty.multiple) {
    const previousValues = res[currentProperty.name] || []

    if (!value.length) {
      // If the property is required but not found, throw an error
      if (currentProperty.required && !previousValues.toString().length) {
        throw new Error(`Property ${currentProperty.name} required in URL but not found`)
      }
      // if we don't find a match, consider this property done but pass the current string unchanged
      return destructure(splitUrl, properties.slice(1),
        { ...res, [currentProperty.name]: previousValues },
      )
    } else {
      const collection = [...previousValues, value]

      // If the value isn't a full-string match, consider this property done but pass the current string to
      // the next property too. Optionally, use a matchValue property on the
      // currentProperty to convert to string before length comparison
      const length = currentProperty.matchValue
        ? currentProperty.matchValue(value).length
        : value.toString().length

      if (length !== currentString.length) {
        return destructure(
          splitUrl,
          properties.slice(1),
          { ...res, [currentProperty.name]: collection },
        )
      }
      // Otherwise, consider the string done but the property not
      return destructure(
        restOfUrl,
        properties,
        { ...res, [currentProperty.name]: collection },
      )
    }
  } else {
    // If the property is required but not found, throw an error
    if (!value.toString().length) {
      if (currentProperty.required) {
        throw new Error(`Property ${currentProperty.name} required in URL but not found`)
      }
      // if we don't find a match, consider this property done but pass the current string unchanged
      return destructure(splitUrl, properties.slice(1), { ...res, [currentProperty.name]:
      value })
    }
    // If the property is singular, consider this string and property done
    return destructure(restOfUrl, properties.slice(1), { ...res, [currentProperty.name]:
      value })
  }
}

/**
 * Splits a URL and passes to destructure to destructure the whole URL
 * @param {String} url - URL to destructure
 * @returns {Object}
 */
const destructureCloudinaryUrl = (url) => destructure(url.split('/'), sequencedProperties)

/**
 * A class that takes a cloudinary URL and parses it for modification and
 * interrogation. Has a couple of convenience methods (documented below) for
 * common tasks.
 */
export class CloudinaryURL {
  constructor(url) {
    const destructured = destructureCloudinaryUrl(url)
    this.proto = destructured.proto
    this.baseUrl = destructured.baseUrl
    this.cloudName = destructured.cloudName
    this.assetType = destructured.assetType
    this.deliveryType = destructured.deliveryType
    this.version = destructured.version
    this.publicId = destructured.publicId
    this.extension = destructured.extension
    this.transformations = destructured.transformations
  }

  // Add a new transform pipeline
  addPipeline() {
    this.transformations = [...this.transformations, []]
    return this
  }

  // Set value of a step with a given transform name
  setStepValueByTransformName(name, value, pipelineIndex) {
    this.transformations = setStepValueByTransformName(this.transformations, name, value, pipelineIndex)
    return this
  }

  // Get value of a step with a given transform name
  getStepValueByTransformName(name, pipelineIndex) {
    return getStepValueByTransformName(this.transformations, name, pipelineIndex)
  }

  // Remove a whole pipeline that contains a named transform
  removePipelineByTransformName(name, pipelineIndex) {
    this.transformations = removePipelineByTransformName(this.transformations, name, pipelineIndex)
    return this
  }

  // Get instance properties in proper order to build a URL
  getUrlParts() {
    return sequencedProperties.reduce((setProperties, item) => {
      const part = this[item.name]

      switch (item.name) {
        case 'proto':
        case '_blank':
        case 'extension':
          return setProperties

        case 'publicId':
          return [...setProperties, part.join('/')]

        case 'transformations':
          return [
            ...setProperties,
            part.map(par => par.map(step => `${step.transformCode}_${step.transformValue}`).join(',')).join('/'),
          ]

        default:
          return part ? [...setProperties, part] : setProperties
      }
    }, [])
  }

  // Get a URL string that displays an image for video URLs.
  imageString() {
    // Clone this object to a new object
    const imageStringURL = Object.assign(Object.create(Object.getPrototypeOf(this)), this)
    // Then remove any pipelines that contain format, add a blank pipeline at
    // the end, set both quality and format to auto on that pipeline and use
    // this to build + return an image URL
    // note: as per docs, a URL containing format auto and no file extension is
    // rendered as an image
    imageStringURL
      .removePipelineByTransformName('format')
      .addPipeline()
      .setStepValueByTransformName('quality', 'auto', -1)
      .setStepValueByTransformName('format', 'auto', -1)

    const urlParts = imageStringURL.getUrlParts()
    const proto = imageStringURL.proto || 'https:'
    const imageUrl = `${proto}//${urlParts.join('/')}`

    return imageUrl
  }

  // Get the cloudinary URL for this instance
  toString() {
    const urlParts = this.getUrlParts()
    const proto = this.proto || 'https:'
    const extension = this.extension ? `.${this.extension}` : ''
    const url = `${proto}//${urlParts.join('/')}${extension}`
    return url
  }

  // Get a Shopify-like video object for this instance
  toVideoObject() {
    const format = this.extension

    return {
      coverImage: {
        src: this.imageString(),
      },
      'sources': [
        {
          format,
          'url': this.toString(),
        },
      ],
    }

  }
}
